import { useMemo } from 'react';
import AirchainAPI from '../../utils/airchain-api';

/**
 * Every script operation, bound to the operator's session. The result is memoised on
 * (restreamer, scopeId) so its functions keep their identity across the page's once-a-second
 * re-render -- components that list them as effect dependencies must never see new ones.
 * scopeId keys import drafts: a channel id, or 'library' for the tenant-level page.
 */
export function useScriptOperations(restreamer, scopeId) {
 return useMemo(() => {
  const filenames = new Map();
  const token = () => restreamer.GetAccessToken();
  return {
   createImport: async ({ filename, text }) => {
    const draft = await AirchainAPI.CreateScriptImport(token(), { filename, text });
    filenames.set(`${scopeId}:${draft.import_id}`, draft.filename);
    const preview = draft.preview;
    return {
     importId: draft.import_id, text: preview.text,
     valid: typeof preview.text === 'string' && /[\p{L}\p{N}]/u.test(preview.text),
     warnings: preview.warnings.map((warning, index) => ({
      id: `${index}:${warning.code}`, code: warning.code, line: warning.source_line,
      message: `${warning.code}${warning.source_line ? ` (line ${warning.source_line})` : ''}`,
     })),
     exclusions: (preview.rows || []).flatMap(row => (row.exclusions || []).map(exclusion => ({
      text: exclusion.text, reason: exclusion.reason, line: row.source_line,
     }))),
    };
   },
   commitImport: async ({ importId, text, acknowledgedWarnings }) => {
    const result = await AirchainAPI.CommitScriptImport(importId, token(), {
     title: filenames.get(`${scopeId}:${importId}`) || 'Script', edited_text: text,
     acknowledged_warnings: [...new Set(acknowledgedWarnings.map(id => id.slice(id.indexOf(':') + 1)))],
    });
    filenames.delete(`${scopeId}:${importId}`);
    return result;
   },
   listScripts: async () => (await AirchainAPI.ListScripts(token())).map(script => ({
    id: script.script_id, name: `${script.title} (version ${script.version})`,
   })),
   listScriptVersions: async () => AirchainAPI.ListScripts(token()),
   getRundown: async (channelId) => {
    const result = await AirchainAPI.GetScriptRundown(channelId, token());
    return { revision: result.revision, entries: result.entries };
   },
   saveRundown: async (channelId, body) => {
    try {
     const result = await AirchainAPI.SaveScriptRundown(channelId, token(), body);
     return { revision: result.revision, entries: result.entries };
    } catch (failure) {
     // Surface the operator-visible conflict shape ScriptRundown branches on.
     if (failure && failure.status === 409) {
      const conflict = new Error('rundown conflict');
      conflict.status = 409;
      throw conflict;
     }
     throw failure;
    }
   },
   getFollowing: async (channelId) => AirchainAPI.GetScriptFollowing(channelId, token()),
   getCaptionsState: async (channelId) => (await AirchainAPI.GetCaptionsStatus(channelId, token())).state,
   activate: async (channelId, mode) => AirchainAPI.PutScriptGateway(channelId, token(), { mode }),
   deactivate: async (channelId) => AirchainAPI.DeleteScriptGateway(channelId, token()),
   control: async (channelId, body) => AirchainAPI.PostScriptControl(channelId, token(), body),
  };
 }, [restreamer, scopeId]);
}
